/**
 * Rich color/variant picker modal. Opens when the user taps the "Color" title
 * on a product detail page. Mirrors the SHEIN-style picker:
 *   - top: horizontal strip of the SELECTED color's images (peek of the next)
 *   - middle: grid of color cards (variant.image instead of swatch + name)
 *   - bottom: size + style chips for the selected color (if present)
 *   - footer: Add to Cart sticky button (pinned to the sheet's visible bottom, after every chip)
 *
 * Render via createPortal so the modal escapes any parent stacking context
 * (so it can sit over both the page's sticky CTA and the top navbar).
 *
 * IT CHOOSES NOTHING FOR THE SHOPPER (Swe Sosetta run 3, panel S2, 2026-10-26). The sheet used to take the picks it
 * was handed as chosen — and every caller handed it the page's first variant, shown so the page has a photo — so the
 * cookies' flavour sheet opened «اللون · دبي» with دبي selected and its add live, and one tap put a flavour nobody
 * chose in the cart. Now the picks it is handed are only where it STARTS (the callers pass what the shopper actually
 * chose, or nothing), the sheet keeps its own picks from there, a choice with one option is the only thing it selects
 * by itself, and the add waits until every other choice is made. Each heading is the product's own choice name
 * (نكهة, الحجم, العبوة) — never the storefront's generic «اللون» / «المقاس» over a flavour or a size.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import AnimatedModal from '../../Components/AnimatedModal';
import { axisNameIn, localizedProduct, optionValueShown } from '../../utils/shopperText';
import { choiceWidget, isColourChoice, tilePicture, valuesWithoutOwnPicture } from '../../utils/optionPictures';
import { swatchColour, swatchLetter } from '../../utils/colourSwatch';
import styles from './colorPickerModal.module.css';

function uniqOf(variants, key) {
  const set = new Set();
  for (const v of variants ?? []) if (v[key]) set.add(v[key]);
  return Array.from(set);
}

const ColorPickerModal = ({
  open = true,
  product: shelfProduct,
  /* What the shopper has ALREADY chosen, to start from — never a value shown for want of a choice. The setters hear
   * each tap too, so the page behind can show the photos of what is being looked at. */
  pickedColor: chosenColor, setPickedColor: tellColor,
  pickedSize: chosenSize,  setPickedSize: tellSize,
  pickedStyle: chosenStyle, setPickedStyle: tellStyle,
  onClose,
  onAddToCart,
  /** Which door opened the sheet: 'buy' finishes at the checkout, 'add' in the cart (the page decides; this names the button). */
  cta = 'add',
  t,
}) => {
  const { i18n } = useTranslation();
  const product = localizedProduct(shelfProduct, i18n?.language);
  const variants = product?.variants ?? [];
  const valueLabel = (slot, v) => optionValueShown(product, slot, v, i18n?.language, t);
  const colorLabel = (c) => valueLabel('color', c);
  /* The sheet's own picks, started from the shopper's choices each time it opens. */
  const [pickedColor, setPickedColorHere] = useState(chosenColor ?? null);
  const [pickedSize, setPickedSizeHere] = useState(chosenSize ?? null);
  const [pickedStyle, setPickedStyleHere] = useState(chosenStyle ?? null);
  useEffect(() => {
    if (!open) return;
    setPickedColorHere(chosenColor ?? null);
    setPickedSizeHere(chosenSize ?? null);
    setPickedStyleHere(chosenStyle ?? null);
    // Only on opening: while it is open, the sheet's picks are the shopper's taps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const setPickedColor = (v) => { setPickedColorHere(v); tellColor?.(v); };
  const setPickedSize = (v) => { setPickedSizeHere(v); tellSize?.(v); };
  const setPickedStyle = (v) => { setPickedStyleHere(v); tellStyle?.(v); };
  /** A choice's heading: the product's own name for it, else the storefront's generic word. */
  const headingOf = (slot, key, fallback) => axisNameIn(product, slot, i18n?.language) || t(key, { defaultValue: fallback });

  // One representative variant per color (first one). Used for both the
  // card grid AND deriving the top image strip when a color is selected.
  // In the payload's order: the server reads the options by their display order (round C: S2, W5).
  const colorCards = useMemo(() => {
    const seen = new Map();
    for (const v of variants) {
      const c = v.color || '__nocolor__';
      if (!seen.has(c)) seen.set(c, v);
    }
    return Array.from(seen.values()).map((v) => ({
      color: v.color || null,
      label: v.color
        ? optionValueShown(product, 'color', v.color, i18n?.language, t)
        : t('productDetail.defaultColor', { defaultValue: 'Default' }),
      image: v.images?.[0]?.url ?? null,
      variantId: v.id,
    }));
  }, [variants, t, product, i18n?.language]);

  /* The colours (flavours…) with no picture of their own — sharing one, or none (panel S5): words, not photos, and
   * choosing one shows the product's own pictures, never another option's. */
  const shared = useMemo(() => valuesWithoutOwnPicture(product, 'color'), [product]);
  /* ONE WIDGET PER CHOICE (Swe Sosetta run 5, round C: S2; fix round 1, #2), as on the page: every value a card when any
   * has a picture of its own, else every value a word. A card for a value with no photo of its own shows the product's
   * picture on a TEXT choice, and its colour swatch on a COLOR choice (a colour sharing a photo keeps it). */
  const colourWidget = choiceWidget(product, 'color', colorCards.map((c) => c.color).filter(Boolean));

  // Images of the currently-picked color (falls back to the first color that
  // has images) — shown as a swipeable strip at the top so the buyer sees the
  // product photos, not just the small per-color card thumbnails.
  const selectedColorImages = useMemo(() => {
    const seen = new Set();
    const out = [];
    const collectImages = (list) => {
      for (const img of list ?? []) {
        const key = img?.id || img?.url;
        if (key && !seen.has(key)) { seen.add(key); out.push(img); }
      }
    };
    const collect = (list) => { for (const v of list) collectImages(v.images); };
    if (pickedColor != null && shared.has(pickedColor)) {
      collectImages(product?.images);
      if (out.length) return out;
    }
    const pool = pickedColor != null ? variants.filter((v) => v.color === pickedColor) : variants;
    collect(pool);
    if (out.length === 0) collect(variants); // fallback when the color has no own photos
    return out;
  }, [variants, pickedColor, shared, product]);

  // Size/style options that are AVAILABLE for the currently-picked color.
  // If picking yellow → only show sizes that yellow exists in. Out-of-stock
  // gets visually disabled but still tappable so the user knows it's there.
  const variantsOfColor = useMemo(
    () => variants.filter((v) => !pickedColor || v.color === pickedColor),
    [variants, pickedColor],
  );
  const sizeOptions  = uniqOf(variantsOfColor, 'size');
  const styleOptions = uniqOf(variantsOfColor, 'style');

  const resolvedVariant = useMemo(() => {
    if (colorCards.length > 1 && pickedColor == null && colorCards[0]?.color != null) {
      return null;
    }
    if (sizeOptions.length > 1 && !pickedSize) return null;
    if (styleOptions.length > 1 && !pickedStyle) return null;
    return variants.find((v) =>
      (colorCards.length <= 1 || v.color === pickedColor || (pickedColor == null && !v.color)) &&
      (sizeOptions.length <= 1 || v.size === pickedSize) &&
      (styleOptions.length <= 1 || v.style === pickedStyle),
    ) ?? null;
  }, [variants, colorCards, pickedColor, pickedSize, pickedStyle, sizeOptions, styleOptions]);

  // When a dimension has only one value for the picked color, select it automatically — one option is no choice.
  // A pick the new colour does not offer is let go of, never swapped for another the shopper did not tap.
  const sizeKey = sizeOptions.join('\u0000');
  const styleKey = styleOptions.join('\u0000');
  useEffect(() => {
    if (sizeOptions.length === 1 && pickedSize !== sizeOptions[0]) {
      setPickedSizeHere(sizeOptions[0]);
    } else if (sizeOptions.length > 1 && pickedSize && !sizeOptions.includes(pickedSize)) {
      setPickedSizeHere(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeKey, pickedSize]);

  useEffect(() => {
    if (styleOptions.length === 1 && pickedStyle !== styleOptions[0]) {
      setPickedStyleHere(styleOptions[0]);
    } else if (styleOptions.length > 1 && pickedStyle && !styleOptions.includes(pickedStyle)) {
      setPickedStyleHere(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleKey, pickedStyle]);

  /* THE DISABLED BUTTON NAMES WHAT IS MISSING (run 4, round B: S5): «أضف إلى السلة» greyed out under four flavours
   * said nothing about why. The first choice still open, in the product's own name for it. */
  let missingChoice = null;
  if (colorCards.length > 1 && pickedColor == null && colorCards[0]?.color != null) missingChoice = headingOf('color', 'productDetail.color', 'Color');
  else if (sizeOptions.length > 1 && !pickedSize) missingChoice = headingOf('size', 'productDetail.size', 'Size');
  else if (styleOptions.length > 1 && !pickedStyle) missingChoice = headingOf('style', 'productDetail.style', 'Style');

  if (!product) return null;

  const doorWord = cta === 'buy'
    ? t('productDetail.buyNow', { defaultValue: 'Buy now' })
    : t('productDetail.addToCart', { defaultValue: 'Add to Cart' });

  return (
    <AnimatedModal
      open={open}
      onClose={onClose}
      sheetClassName={styles.sheet}
      testId="color-picker-modal"
    >
      {({ requestClose }) => (
        <>
        {/* Top image strip — selected color's images, swipeable */}
        <button
          type="button"
          className={styles.closeBtn}
          onClick={requestClose}
          aria-label={t('common.close', { defaultValue: 'Close' })}
          data-testid="color-picker-modal-close"
        >
          <FiX />
        </button>

        {/* Product title */}
        <div className={styles.header}>
          <h2 className={styles.title}>{product.name}</h2>
        </div>

        {/* Top image strip — the selected color's photos, swipeable */}
        {selectedColorImages.length > 0 && (
          <div className={styles.imageStrip} data-testid="color-picker-image-strip">
            {selectedColorImages.map((img, i) => (
              <div key={img.id || img.url || i} className={styles.imageStripItem}>
                <img src={img.url} alt={product.name} loading="lazy" />
              </div>
            ))}
          </div>
        )}

        {/* ONE VALUE IS A FACT, NOT A CHOICE (run 4, round B: S5): a choice with a single value is its spec line —
          * «الوزن علبة 500 غ» — never a lone selected pill the shopper is left to wonder about. */}
        {colorCards.length === 1 && colorCards[0].color != null && (
          <div className={styles.section} data-testid="color-picker-color-fixed">
            <h3 className={`${styles.sectionLabel} ${styles.specLine}`}>
              {headingOf('color', 'productDetail.color', 'Color')}
              <span className={styles.sectionValue}><bdi>{colorLabel(colorCards[0].color)}</bdi></span>
            </h3>
          </div>
        )}

        {/* Color cards grid — variant image as the card content */}
        {colorCards.length > 1 && (
        <div className={styles.section}>
          <h3 className={styles.sectionLabel}>
            {headingOf('color', 'productDetail.color', 'Color')}
            {pickedColor != null && (
              <>
                <span className={styles.sectionDot} aria-hidden>·</span>
                <span className={styles.sectionValue} data-testid="color-picker-chosen"><bdi>{colorLabel(pickedColor)}</bdi></span>
              </>
            )}
          </h3>
          {colourWidget === 'tiles' ? (
          <div className={styles.colorGrid} data-testid="color-picker-grid">
            {colorCards.map((c) => {
              // A colour with no photo of its own is its swatch, never the gallery's first photo (fix round 1, #2).
              const colourSwatch = c.color != null && isColourChoice(product, 'color');
              const image = (c.color != null ? tilePicture(product, 'color', c.color, shared) : '') || (colourSwatch ? '' : c.image);
              return (
              <button
                key={c.color ?? c.variantId}
                type="button"
                className={`${styles.colorCard} ${pickedColor === c.color ? styles.colorCardSelected : ''}`}
                aria-pressed={pickedColor === c.color}
                onClick={(e) => {
                  setPickedColor(c.color);
                  e.currentTarget.blur();
                }}
                data-testid={`color-picker-card-${(c.color || 'default').toLowerCase()}`}
              >
                <div className={styles.colorCardImg}>
                  {image
                    ? <img src={image} alt={c.label} loading="lazy" />
                    : colourSwatch
                      ? (swatchColour(product, 'color', c.color)
                        ? <span className={styles.colorCardSwatch} style={{ background: swatchColour(product, 'color', c.color) }} data-testid="color-picker-swatch" data-swatch="colour" aria-hidden />
                        : <span className={`${styles.colorCardSwatch} ${styles.colorCardSwatchNeutral}`} data-testid="color-picker-swatch" data-swatch="neutral" aria-hidden>{swatchLetter(c.label)}</span>)
                      : <span>📦</span>}
                </div>
                <span className={styles.colorCardLabel}><bdi>{c.label}</bdi></span>
              </button>
              );
            })}
          </div>
          ) : (
            <div className={styles.chipRow} data-testid="color-picker-words">
              {colorCards.map((c) => (
                <button
                  key={c.color ?? c.variantId}
                  type="button"
                  className={`${styles.chip} ${pickedColor === c.color ? styles.chipSelected : ''}`}
                  aria-pressed={pickedColor === c.color}
                  onClick={(e) => {
                    setPickedColor(c.color);
                    e.currentTarget.blur();
                  }}
                  data-testid={`color-picker-card-${String(c.color || 'default').toLowerCase()}`}
                >
                  <bdi>{c.label}</bdi>
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {sizeOptions.length === 1 && (
          <div className={styles.section} data-testid="color-picker-size-fixed">
            <h3 className={`${styles.sectionLabel} ${styles.specLine}`}>
              {headingOf('size', 'productDetail.size', 'Size')}
              <span className={styles.sectionValue} data-testid={`color-picker-size-${sizeOptions[0]}`}><bdi>{valueLabel('size', sizeOptions[0])}</bdi></span>
            </h3>
          </div>
        )}

        {/* Size selector (chips) */}
        {sizeOptions.length > 1 && (
          <div className={styles.section}>
            <h3 className={styles.sectionLabel}>
              {headingOf('size', 'productDetail.size', 'Size')}
              {pickedSize && <span className={styles.sectionValue}><bdi>{valueLabel('size', pickedSize)}</bdi></span>}
            </h3>
            <div className={styles.chipRow}>
              {sizeOptions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`${styles.chip} ${pickedSize === s ? styles.chipSelected : ''}`}
                  aria-pressed={pickedSize === s}
                  onClick={(e) => {
                    setPickedSize(s);
                    e.currentTarget.blur();
                  }}
                  data-testid={`color-picker-size-${s}`}
                >
                  <bdi>{valueLabel('size', s)}</bdi>
                </button>
              ))}
            </div>
          </div>
        )}

        {styleOptions.length === 1 && (
          <div className={styles.section} data-testid="color-picker-style-fixed">
            <h3 className={`${styles.sectionLabel} ${styles.specLine}`}>
              {headingOf('style', 'productDetail.style', 'Style')}
              <span className={styles.sectionValue} data-testid={`color-picker-style-${String(styleOptions[0]).toLowerCase()}`}><bdi>{valueLabel('style', styleOptions[0])}</bdi></span>
            </h3>
          </div>
        )}

        {/* Style selector (chips) */}
        {styleOptions.length > 1 && (
          <div className={styles.section}>
            <h3 className={styles.sectionLabel}>
              {headingOf('style', 'productDetail.style', 'Style')}
              {pickedStyle && <span className={styles.sectionValue}><bdi>{valueLabel('style', pickedStyle)}</bdi></span>}
            </h3>
            <div className={styles.chipRow}>
              {styleOptions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`${styles.chip} ${pickedStyle === s ? styles.chipSelected : ''}`}
                  aria-pressed={pickedStyle === s}
                  onClick={(e) => {
                    setPickedStyle(s);
                    e.currentTarget.blur();
                  }}
                  data-testid={`color-picker-style-${s.toLowerCase()}`}
                >
                  <bdi>{valueLabel('style', s)}</bdi>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer: Add to cart — the sheet's last child, sticky at its visible bottom (see the stylesheet). */}
        <div className={styles.footer} data-testid="color-picker-footer">
          <button
            type="button"
            className={`${styles.addBtn} ${!resolvedVariant && missingChoice ? styles.addBtnWaiting : ''}`}
            disabled={!resolvedVariant}
            onClick={() => {
              if (!resolvedVariant) return;
              onAddToCart(resolvedVariant);
              requestClose();
            }}
            data-testid="color-picker-add-to-cart"
            data-cta={cta}
          >
            {!resolvedVariant && missingChoice ? (
              <>
                <span className={styles.addBtnDoor}>{doorWord}</span>
                <span className={styles.addBtnMissing} data-testid="color-picker-missing">
                  {t('productDetail.chooseFirst', { defaultValue: 'Choose {{axis}}', axis: missingChoice })}
                </span>
              </>
            ) : doorWord}
          </button>
        </div>
        </>
      )}
    </AnimatedModal>
  );
};

export default ColorPickerModal;
